import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  AreaChart,
  Area,
} from "recharts";
import { toast } from "sonner";
import {
  Users,
  ClipboardList,
  Calendar,
  TrendingUp,
  LogOut,
  Loader2,
  Download,
  FileSpreadsheet,
  Eye,
  Clock,
  MousePointer,
  FileText,
  Activity,
  UserCheck,
  RefreshCw,
  MessageSquare,
  Send,
  Sparkles,
} from "lucide-react";
import axios from "axios";
import { Textarea } from "@/components/ui/textarea";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const COLORS = ["#10B981", "#0F172A", "#64748B", "#F59E0B", "#EF4444", "#8B5CF6", "#EC4899"];

const Admin = () => {
  const navigate = useNavigate();
  const [token, setToken] = useState(localStorage.getItem("admin_token"));
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [loginData, setLoginData] = useState({ username: "", password: "" });
  const [stats, setStats] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [subscribers, setSubscribers] = useState([]);
  const [surveys, setSurveys] = useState([]);
  const [etfQuizStats, setEtfQuizStats] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  // Founder's Take state
  const [founderComments, setFounderComments] = useState([]);
  const [newComment, setNewComment] = useState({ author: "Arsh Dharani", text: "" });
  const [submittingComment, setSubmittingComment] = useState(false);
  const [generatingBlog, setGeneratingBlog] = useState(false);

  // Export subscribers to CSV/Excel
  const exportToExcel = () => {
    if (subscribers.length === 0) {
      toast.error("No subscribers to export");
      return;
    }
    
    const headers = ["Email", "Phone", "Source", "SMS Consent", "Date Subscribed"];
    const csvContent = [
      headers.join(","),
      ...subscribers.map(sub => [
        sub.email || "",
        sub.phone || "",
        sub.source || "website",
        sub.sms_consent ? "Yes" : "No",
        sub.created_at ? new Date(sub.created_at).toLocaleDateString() : ""
      ].map(field => `"${field}"`).join(","))
    ].join("\n");
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `compoundr_subscribers_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    toast.success(`Exported ${subscribers.length} subscribers to Excel!`);
  };

  useEffect(() => {
    if (token) {
      fetchData();
      
      // Auto-refresh every 2 minutes (120000ms)
      const refreshInterval = setInterval(() => {
        fetchData();
        console.log("Admin data auto-refreshed");
      }, 120000);
      
      return () => clearInterval(refreshInterval);
    }
  }, [token]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [statsRes, usersRes, surveysRes, analyticsRes, etfQuizRes, marketRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/stats`, { headers }),
        axios.get(`${API_URL}/api/users`, { headers }),
        axios.get(`${API_URL}/api/survey`, { headers }),
        axios.get(`${API_URL}/api/analytics/stats`, { headers }),
        axios.get(`${API_URL}/api/etf-quiz/stats`, { headers }),
        axios.get(`${API_URL}/api/market-update/today`).catch(() => ({ data: {} })),
      ]);
      setStats(statsRes.data);
      setSubscribers(usersRes.data); // Now using users instead of subscribers
      setSurveys(surveysRes.data);
      setAnalytics(analyticsRes.data);
      setEtfQuizStats(etfQuizRes.data);
      setFounderComments(marketRes.data?.founder_comments || []);
      setLastUpdated(new Date());
    } catch (error) {
      if (error.response?.status === 401) {
        localStorage.removeItem("admin_token");
        setToken(null);
        toast.error("Session expired. Please login again.");
      } else {
        toast.error("Failed to fetch data");
      }
    } finally {
      setLoading(false);
    }
  };

  const refreshAnalytics = async () => {
    setRefreshing(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [usersRes, analyticsRes] = await Promise.all([
        axios.get(`${API_URL}/api/users`, { headers }),
        axios.get(`${API_URL}/api/analytics/stats`, { headers }),
      ]);
      setSubscribers(usersRes.data);
      setAnalytics(analyticsRes.data);
      setLastUpdated(new Date());
      toast.success("Data refreshed!");
    } catch (error) {
      toast.error("Failed to refresh data");
    } finally {
      setRefreshing(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/admin/login`, loginData);
      localStorage.setItem("admin_token", res.data.token);
      setToken(res.data.token);
      toast.success("Welcome back!");
    } catch (error) {
      toast.error("Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("admin_token");
    setToken(null);
    toast.success("Logged out successfully");
  };

  // Submit founder comment
  const handleSubmitComment = async () => {
    if (!newComment.text.trim()) {
      toast.error("Please enter a comment");
      return;
    }

    setSubmittingComment(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(
        `${API_URL}/api/market-update/founder-comment`,
        { author: newComment.author, comment: newComment.text },
        { headers }
      );
      toast.success("Founder's Take published!");
      setNewComment({ ...newComment, text: "" });
      // Refresh to get new comments
      const marketRes = await axios.get(`${API_URL}/api/market-update/today`);
      setFounderComments(marketRes.data?.founder_comments || []);
    } catch (error) {
      toast.error("Failed to publish comment");
    } finally {
      setSubmittingComment(false);
    }
  };

  // Trigger blog generation
  const handleGenerateBlog = async () => {
    setGeneratingBlog(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API_URL}/api/blog/auto-generate`, {}, { headers });
      toast.success("Blog generation started! Check the blog page in a minute.");
    } catch (error) {
      toast.error("Failed to trigger blog generation");
    } finally {
      setGeneratingBlog(false);
    }
  };

  // Login form
  if (!token) {
    return (
      <div className="min-h-screen gradient-mesh flex items-center justify-center p-4">
        <Card className="w-full max-w-md border-0 shadow-floating">
          <CardHeader className="text-center">
            <CardTitle className="font-heading text-2xl">Admin Login</CardTitle>
            <p className="text-slate-600 text-sm">Compoundr. Dashboard</p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <Label htmlFor="username">Email</Label>
                <Input
                  id="username"
                  type="email"
                  placeholder="compoundrca@gmail.com"
                  value={loginData.username}
                  onChange={(e) => setLoginData({ ...loginData, username: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  value={loginData.password}
                  onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                  required
                />
              </div>
              <Button type="submit" className="w-full bg-emerald-500 hover:bg-emerald-600" disabled={loading}>
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Login"}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading && !stats) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  // Prepare chart data
  const pageViewsData = analytics?.page_views_by_page 
    ? Object.entries(analytics.page_views_by_page).map(([page, count]) => ({
        page: page.replace('/', '') || 'Home',
        views: count,
        avgTime: analytics.avg_time_by_page?.[page] || 0
      }))
    : [];

  const userActivityData = analytics?.user_activity_timeline || [];

  return (
    <div className="min-h-screen bg-slate-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-heading font-bold text-navy-700">Admin Dashboard</h1>
            <p className="text-slate-600">Compoundr. Analytics & Management</p>
          </div>
          <div className="flex items-center gap-3">
            <Button 
              variant="outline" 
              onClick={refreshAnalytics}
              disabled={refreshing}
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button variant="outline" onClick={handleLogout}>
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>

        {/* Active Users Stats - Top Row */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4 mb-8">
          <Card className="border-0 shadow-card bg-gradient-to-br from-emerald-500 to-emerald-600 text-white">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-emerald-100 text-sm">Active Now</p>
                  <p className="text-3xl font-bold">{analytics?.active_users_now || 0}</p>
                </div>
                <Activity className="w-8 h-8 text-emerald-200" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-card">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-500 text-sm">Past Hour</p>
                  <p className="text-2xl font-bold text-navy-700">{analytics?.users_past_hour || 0}</p>
                </div>
                <Clock className="w-6 h-6 text-slate-400" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-card">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-500 text-sm">Past 24h</p>
                  <p className="text-2xl font-bold text-navy-700">{analytics?.users_past_24h || 0}</p>
                </div>
                <UserCheck className="w-6 h-6 text-slate-400" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-card">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-500 text-sm">Past 7 Days</p>
                  <p className="text-2xl font-bold text-navy-700">{analytics?.users_past_7d || 0}</p>
                </div>
                <Users className="w-6 h-6 text-slate-400" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-card">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-500 text-sm">Past Month</p>
                  <p className="text-2xl font-bold text-navy-700">{analytics?.users_past_month || 0}</p>
                </div>
                <TrendingUp className="w-6 h-6 text-slate-400" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Extended Time Range Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card className="border-0 shadow-card">
            <CardContent className="pt-4 pb-4">
              <p className="text-slate-500 text-xs">6 Hours</p>
              <p className="text-xl font-bold text-navy-700">{analytics?.users_past_6h || 0} users</p>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-card">
            <CardContent className="pt-4 pb-4">
              <p className="text-slate-500 text-xs">12 Hours</p>
              <p className="text-xl font-bold text-navy-700">{analytics?.users_past_12h || 0} users</p>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-card">
            <CardContent className="pt-4 pb-4">
              <p className="text-slate-500 text-xs">3 Days</p>
              <p className="text-xl font-bold text-navy-700">{analytics?.users_past_3d || 0} users</p>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-card">
            <CardContent className="pt-4 pb-4">
              <p className="text-slate-500 text-xs">3 Months</p>
              <p className="text-xl font-bold text-navy-700">{analytics?.users_past_3months || 0} users</p>
            </CardContent>
          </Card>
        </div>

        {/* Main Stats Cards */}
        <div className="grid md:grid-cols-4 gap-6 mb-8">
          <Card className="border-0 shadow-card">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
                  <Users className="w-6 h-6 text-emerald-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-navy-700">{stats?.total_subscribers || 0}</p>
                  <p className="text-sm text-slate-500">Total Subscribers</p>
                </div>
              </div>
              <Badge className="mt-3 bg-emerald-100 text-emerald-700">
                +{analytics?.new_subscribers_today || 0} today
              </Badge>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-card">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                  <Eye className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-navy-700">{analytics?.total_page_views || 0}</p>
                  <p className="text-sm text-slate-500">Total Page Views</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-card">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                  <ClipboardList className="w-6 h-6 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-navy-700">{stats?.total_surveys || 0}</p>
                  <p className="text-sm text-slate-500">Quiz Completions</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-card">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                  <Calendar className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-navy-700">{stats?.total_seminars || 0}</p>
                  <p className="text-sm text-slate-500">Seminar Registrations</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Charts Row */}
        <div className="grid lg:grid-cols-2 gap-6 mb-8">
          {/* User Activity Timeline */}
          <Card className="border-0 shadow-card">
            <CardHeader>
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <Activity className="w-5 h-5 text-emerald-500" />
                User Activity Timeline
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-[250px]">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={userActivityData}>
                    <XAxis dataKey="period" />
                    <YAxis />
                    <Tooltip />
                    <Area type="monotone" dataKey="users" stroke="#10B981" fill="#10B98133" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Page Views */}
          <Card className="border-0 shadow-card">
            <CardHeader>
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <Eye className="w-5 h-5 text-purple-500" />
                Page Views & Time Spent
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-[250px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={pageViewsData} layout="vertical">
                    <XAxis type="number" />
                    <YAxis type="category" dataKey="page" width={80} />
                    <Tooltip formatter={(value, name) => [name === 'avgTime' ? `${value}s` : value, name === 'avgTime' ? 'Avg Time' : 'Views']} />
                    <Bar dataKey="views" fill="#8B5CF6" name="Views" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Top Downloads & Clicks */}
        <div className="grid lg:grid-cols-3 gap-6 mb-8">
          {/* Top Downloads */}
          <Card className="border-0 shadow-card">
            <CardHeader>
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <Download className="w-5 h-5 text-emerald-500" />
                Top Downloads
              </CardTitle>
            </CardHeader>
            <CardContent>
              {analytics?.top_downloads?.length > 0 ? (
                <div className="space-y-3">
                  {analytics.top_downloads.map((item, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <span className="text-sm font-medium text-navy-700">{item.name}</span>
                      <Badge>{item.count} downloads</Badge>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-slate-500 text-center py-8">No downloads yet</p>
              )}
            </CardContent>
          </Card>

          {/* Top Clicks */}
          <Card className="border-0 shadow-card">
            <CardHeader>
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <MousePointer className="w-5 h-5 text-amber-500" />
                Top Clicked Elements
              </CardTitle>
            </CardHeader>
            <CardContent>
              {analytics?.top_clicks?.length > 0 ? (
                <div className="space-y-3">
                  {analytics.top_clicks.map((item, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <span className="text-sm font-medium text-navy-700 truncate max-w-[150px]">{item.element}</span>
                      <Badge variant="secondary">{item.count} clicks</Badge>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-slate-500 text-center py-8">No click data yet</p>
              )}
            </CardContent>
          </Card>

          {/* Top Blogs by Reading Time */}
          <Card className="border-0 shadow-card">
            <CardHeader>
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <FileText className="w-5 h-5 text-blue-500" />
                Top Blogs (Reading Time)
              </CardTitle>
            </CardHeader>
            <CardContent>
              {analytics?.top_blogs_by_time?.length > 0 ? (
                <div className="space-y-3">
                  {analytics.top_blogs_by_time.map((item, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <div>
                        <span className="text-sm font-medium text-navy-700 block truncate max-w-[120px]">{item.slug}</span>
                        <span className="text-xs text-slate-500">{item.readers} readers</span>
                      </div>
                      <Badge className="bg-blue-100 text-blue-700">{Math.round(item.total_time / 60)}m read</Badge>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-slate-500 text-center py-8">No blog data yet</p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Tabs for Detailed Data */}
        <Tabs defaultValue="users" className="space-y-6">
          <TabsList className="bg-white shadow-card p-1">
            <TabsTrigger value="users">Users</TabsTrigger>
            <TabsTrigger value="surveys">Quiz Results</TabsTrigger>
            <TabsTrigger value="etf-quiz">ETF Quiz</TabsTrigger>
            <TabsTrigger value="sources">Traffic Sources</TabsTrigger>
            <TabsTrigger value="content">Content</TabsTrigger>
          </TabsList>

          <TabsContent value="users">
            <Card className="border-0 shadow-card">
              <CardContent className="pt-6">
                <div className="flex justify-between items-center mb-4">
                  <div>
                    <p className="text-sm text-slate-500">
                      Showing {Math.min(50, subscribers.length)} of {subscribers.length} users
                    </p>
                    {lastUpdated && (
                      <p className="text-xs text-slate-400">
                        Last updated: {lastUpdated.toLocaleTimeString()} (auto-refreshes every 2 min)
                      </p>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      onClick={refreshAnalytics}
                      disabled={refreshing}
                    >
                      <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
                      Refresh Now
                    </Button>
                    <Button onClick={exportToExcel} className="bg-emerald-500 hover:bg-emerald-600">
                      <FileSpreadsheet className="w-4 h-4 mr-2" />
                      Export to Excel
                    </Button>
                  </div>
                </div>
                
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Email</TableHead>
                      <TableHead>Phone</TableHead>
                      <TableHead>Investor Type</TableHead>
                      <TableHead>Joined</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {subscribers.slice(0, 50).map((user) => (
                      <TableRow key={user.id}>
                        <TableCell className="font-medium">{user.email}</TableCell>
                        <TableCell>{user.phone || "—"}</TableCell>
                        <TableCell>
                          {user.investor_type ? (
                            <Badge className="bg-emerald-100 text-emerald-700">{user.investor_type}</Badge>
                          ) : (
                            <Badge variant="secondary">Not taken</Badge>
                          )}
                        </TableCell>
                        <TableCell>{new Date(user.created_at).toLocaleDateString()}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                {subscribers.length === 0 && (
                  <p className="text-slate-500 text-center py-8">No users yet</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="surveys">
            <Card className="border-0 shadow-card">
              <CardContent className="pt-6">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Email</TableHead>
                      <TableHead>Financial Situation</TableHead>
                      <TableHead>Knowledge</TableHead>
                      <TableHead>Date</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {surveys.slice(0, 20).map((survey) => (
                      <TableRow key={survey.id}>
                        <TableCell className="font-medium">{survey.email || "Anonymous"}</TableCell>
                        <TableCell>{survey.financial_situation}</TableCell>
                        <TableCell>{survey.knowledge_level}</TableCell>
                        <TableCell>{new Date(survey.created_at).toLocaleDateString()}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                {surveys.length === 0 && (
                  <p className="text-slate-500 text-center py-8">No quiz completions yet</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="etf-quiz">
            <div className="grid md:grid-cols-4 gap-6 mb-6">
              {/* ETF Quiz Summary Cards */}
              <Card className="border-0 shadow-card">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                      <ClipboardList className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-navy-700">{etfQuizStats?.total_quizzes || 0}</p>
                      <p className="text-sm text-slate-500">Total Quizzes</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-0 shadow-card">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
                      <TrendingUp className="w-6 h-6 text-emerald-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-emerald-600">{etfQuizStats?.acceptance_rate || 0}%</p>
                      <p className="text-sm text-slate-500">Acceptance Rate</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-0 shadow-card">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                      <Users className="w-6 h-6 text-purple-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-navy-700">{etfQuizStats?.total_feedback || 0}</p>
                      <p className="text-sm text-slate-500">Feedback Received</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-0 shadow-card">
                <CardContent className="pt-6">
                  <div className="text-center">
                    <p className="text-sm text-slate-500 mb-2">Feedback Breakdown</p>
                    <div className="flex justify-center gap-4">
                      <div>
                        <span className="text-emerald-500 text-lg font-bold">{etfQuizStats?.feedback_breakdown?.yes || 0}</span>
                        <p className="text-xs text-slate-400">Yes</p>
                      </div>
                      <div>
                        <span className="text-amber-500 text-lg font-bold">{etfQuizStats?.feedback_breakdown?.unsure || 0}</span>
                        <p className="text-xs text-slate-400">Unsure</p>
                      </div>
                      <div>
                        <span className="text-red-500 text-lg font-bold">{etfQuizStats?.feedback_breakdown?.no || 0}</span>
                        <p className="text-xs text-slate-400">No</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="grid lg:grid-cols-2 gap-6 mb-6">
              {/* Risk Distribution */}
              <Card className="border-0 shadow-card">
                <CardHeader>
                  <CardTitle className="font-heading text-lg">Risk Bucket Distribution</CardTitle>
                </CardHeader>
                <CardContent>
                  {etfQuizStats?.risk_distribution && Object.keys(etfQuizStats.risk_distribution).length > 0 ? (
                    <div className="h-[250px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={Object.entries(etfQuizStats.risk_distribution).map(([name, value]) => ({ name, value }))}
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                            outerRadius={80}
                            fill="#8884d8"
                            dataKey="value"
                          >
                            {Object.entries(etfQuizStats.risk_distribution).map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  ) : (
                    <p className="text-slate-500 text-center py-8">No quiz data yet</p>
                  )}
                </CardContent>
              </Card>

              {/* Popular ETFs */}
              <Card className="border-0 shadow-card">
                <CardHeader>
                  <CardTitle className="font-heading text-lg">Popular ETF Recommendations</CardTitle>
                </CardHeader>
                <CardContent>
                  {etfQuizStats?.popular_etfs?.length > 0 ? (
                    <div className="space-y-3">
                      {etfQuizStats.popular_etfs.map((etf, idx) => (
                        <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                          <div>
                            <span className="font-bold text-navy-700">{etf.ticker}</span>
                            <span className="text-sm text-slate-500 ml-2">Avg: {etf.avg_allocation}%</span>
                          </div>
                          <Badge className="bg-blue-100 text-blue-700">{etf.count} times</Badge>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-slate-500 text-center py-8">No ETF data yet</p>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Recent Quizzes Table */}
            <Card className="border-0 shadow-card">
              <CardHeader>
                <CardTitle className="font-heading text-lg">Recent Quiz Submissions</CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Portfolio Name</TableHead>
                      <TableHead>Risk Bucket</TableHead>
                      <TableHead>ETFs</TableHead>
                      <TableHead>MER</TableHead>
                      <TableHead>Date</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {etfQuizStats?.recent_quizzes?.slice(0, 10).map((quiz) => (
                      <TableRow key={quiz.id}>
                        <TableCell className="font-medium">{quiz.portfolio_name}</TableCell>
                        <TableCell>
                          <Badge className={
                            quiz.risk_bucket === 'safe' ? 'bg-blue-100 text-blue-700' :
                            quiz.risk_bucket === 'balanced' ? 'bg-amber-100 text-amber-700' :
                            quiz.risk_bucket === 'growth' ? 'bg-emerald-100 text-emerald-700' :
                            'bg-purple-100 text-purple-700'
                          }>
                            {quiz.risk_bucket}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {quiz.etf_allocations?.map(e => e.ticker).join(', ') || '—'}
                        </TableCell>
                        <TableCell>{quiz.weighted_mer}%</TableCell>
                        <TableCell>{new Date(quiz.created_at).toLocaleDateString()}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                {(!etfQuizStats?.recent_quizzes || etfQuizStats.recent_quizzes.length === 0) && (
                  <p className="text-slate-500 text-center py-8">No ETF quiz submissions yet</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="sources">
            <Card className="border-0 shadow-card">
              <CardHeader>
                <CardTitle className="font-heading text-lg">Subscribers by Source</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={Object.entries(stats?.subscribers_by_source || {}).map(([name, value]) => ({ name, value }))}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                        outerRadius={100}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {Object.entries(stats?.subscribers_by_source || {}).map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Content Management Tab */}
          <TabsContent value="content">
            <div className="grid gap-6 md:grid-cols-2">
              {/* Founder's Take Section */}
              <Card className="border-0 shadow-card">
                <CardHeader>
                  <CardTitle className="font-heading text-lg flex items-center gap-2">
                    <MessageSquare className="w-5 h-5 text-emerald-600" />
                    Founder&apos;s Take
                  </CardTitle>
                  <p className="text-sm text-slate-500">
                    Add commentary to today&apos;s Daily Market Update
                  </p>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label htmlFor="author">Author</Label>
                    <select
                      id="author"
                      value={newComment.author}
                      onChange={(e) => setNewComment({ ...newComment, author: e.target.value })}
                      className="w-full mt-1 border rounded-md p-2"
                    >
                      <option value="Arsh Dharani">Arsh Dharani</option>
                      <option value="Ayan Charnia">Ayan Charnia</option>
                    </select>
                  </div>
                  <div>
                    <Label htmlFor="comment">Your Take</Label>
                    <Textarea
                      id="comment"
                      placeholder="Share your thoughts on today's market..."
                      value={newComment.text}
                      onChange={(e) => setNewComment({ ...newComment, text: e.target.value })}
                      rows={4}
                      className="mt-1"
                    />
                  </div>
                  <Button
                    onClick={handleSubmitComment}
                    disabled={submittingComment}
                    className="w-full bg-emerald-600 hover:bg-emerald-700"
                  >
                    {submittingComment ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Send className="w-4 h-4 mr-2" />
                    )}
                    Publish to Daily Market Update
                  </Button>

                  {/* Today's Comments */}
                  {founderComments.length > 0 && (
                    <div className="mt-4 border-t pt-4">
                      <p className="text-sm font-medium text-slate-700 mb-2">Today&apos;s Comments:</p>
                      {founderComments.map((comment, idx) => (
                        <div key={idx} className="bg-slate-50 rounded-lg p-3 mb-2">
                          <p className="text-sm text-slate-700">{comment.comment}</p>
                          <p className="text-xs text-slate-500 mt-1">{comment.author}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Blog Generation Section */}
              <Card className="border-0 shadow-card">
                <CardHeader>
                  <CardTitle className="font-heading text-lg flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-purple-600" />
                    Auto Blog Generator
                  </CardTitle>
                  <p className="text-sm text-slate-500">
                    Generate AI-written educational blog posts
                  </p>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="bg-purple-50 rounded-lg p-4">
                    <p className="text-sm text-purple-700 mb-2">
                      The AI will create a new blog post on topics like:
                    </p>
                    <ul className="text-xs text-purple-600 space-y-1 list-disc list-inside">
                      <li>TFSA & RRSP strategies</li>
                      <li>ETF investing for beginners</li>
                      <li>Budgeting tips for students</li>
                      <li>Canadian market insights</li>
                    </ul>
                  </div>
                  <Button
                    onClick={handleGenerateBlog}
                    disabled={generatingBlog}
                    className="w-full bg-purple-600 hover:bg-purple-700"
                  >
                    {generatingBlog ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Generating...
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-4 h-4 mr-2" />
                        Generate New Blog Post
                      </>
                    )}
                  </Button>
                  <p className="text-xs text-slate-400 text-center">
                    Blog posts are also auto-generated daily at 6 AM EST
                  </p>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default Admin;
